import _ from 'lodash'
import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { P2pCard } from '@/components/p2p/P2pCard'
import { P2pCardSkeleton } from '@/components/p2p/P2pCardSkeleton'
import { ProfileTwoUserIcon } from '@/components/p2p/icons'
import { Footer } from '@/components/layout/footer'
import { PageShell } from '@/components/layout/PageShell'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { P2P_MARKET_SORT_OPTIONS, P2P_MARKET_STATUS_OPTIONS, P2P_MARKETS_PER_PAGE, P2P_MARKETS_SKELETON_COUNT } from '@/config/p2p'
import type { MarketSort, MarketStatus } from '@/types/p2p'
import { useDebounce } from '@/hooks/use-debounce'
import { useInfiniteP2pMarkets } from '@/hooks/use-infinite-p2p-markets'

export default function P2pLegends() {
	const loadMoreRef = useRef<HTMLDivElement>(null)
	const [search, setSearch] = useState('')
	const [sortBy, setSortBy] = useState<MarketSort>(P2P_MARKET_SORT_OPTIONS[0].value)
	const [status, setStatus] = useState<MarketStatus>(P2P_MARKET_STATUS_OPTIONS[0].value)
	const debouncedSearch = useDebounce(search.trim())
	const { markets, isLoading, isLoadingMore, isError, hasMore, loadMore } = useInfiniteP2pMarkets({
		order: sortBy,
		status: status === 'all' ? undefined : status,
		search: debouncedSearch || undefined,
	})

	useEffect(() => {
		const node = loadMoreRef.current

		if (!node || !hasMore || isLoadingMore) {
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					loadMore()
				}
			},
			{ rootMargin: '240px' },
		)

		observer.observe(node)

		return () => observer.disconnect()
	}, [hasMore, isLoadingMore, loadMore, markets.length])

	return (
		<PageShell gap='6' footer={<Footer />}>
			<div className='flex items-center gap-3'>
				<ProfileTwoUserIcon className='text-icon h-7 w-7' />
				<h1 className='text-2xl font-semibold tracking-[-0.12px] text-foreground'>P2P Legends</h1>
			</div>

			{isError ? <p className='text-sm text-muted-foreground'>Unable to load P2P markets right now.</p> : null}

			<div className='grid grid-cols-1 gap-3 rounded-2xl p-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_220px_180px]'>
				<Input
					type='search'
					variant='icon'
					icon={<Search />}
					placeholder='Search markets by keyword'
					aria-label='Search P2P markets by keyword'
					value={search}
					onChange={(event) => setSearch(event.target.value)}
				/>
				<div>
					<Select value={sortBy} onValueChange={(value) => setSortBy(value as MarketSort)}>
						<SelectTrigger
							className='relative h-[58px] flex-col items-start justify-center gap-0.5 rounded-xl pr-10 [&>svg]:absolute [&>svg]:right-3 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2'
							aria-label='Sort markets by'
						>
							<span className='text-xs font-medium text-muted-foreground'>Sort By</span>
							<SelectValue className='text-left' />
						</SelectTrigger>
						<SelectContent>
							{P2P_MARKET_SORT_OPTIONS.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div>
					<Select value={status} onValueChange={(value) => setStatus(value as MarketStatus)}>
						<SelectTrigger
							className='relative h-[58px] flex-col items-start justify-center gap-0.5 rounded-xl pr-10 [&>svg]:absolute [&>svg]:right-3 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2'
							aria-label='Filter markets by status'
						>
							<span className='text-xs font-medium text-muted-foreground'>Status</span>
							<SelectValue className='text-left' />
						</SelectTrigger>
						<SelectContent>
							{P2P_MARKET_STATUS_OPTIONS.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{isLoading ? (
				<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
					{_.times(P2P_MARKETS_SKELETON_COUNT, (index) => (
						<P2pCardSkeleton key={`p2p-market-skeleton-${index}`} />
					))}
				</div>
			) : null}

			{!isLoading && !isError && _.isEmpty(markets) ? (
				<p className='text-center text-sm text-muted-foreground'>
					{debouncedSearch || status !== 'all' ? 'No P2P markets match your filters.' : 'No P2P markets are available right now.'}
				</p>
			) : null}

			{!isLoading && !isError && !_.isEmpty(markets) ? (
				<>
					<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
						{_.map(markets, (market) => (
							<P2pCard key={market.id} market={market} />
						))}
					</div>
					{isLoadingMore ? (
						<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
							{_.times(P2P_MARKETS_PER_PAGE, (index) => (
								<P2pCardSkeleton key={`p2p-market-loading-${index}`} />
							))}
						</div>
					) : null}
				</>
			) : null}

			{!isLoading && !isError && hasMore ? <div ref={loadMoreRef} className='h-px' aria-hidden /> : null}
		</PageShell>
	)
}
